import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { DropdownMenuOptionType } from '@/components/molecules/DropdownMenu/DropdownMenu';
import { DropdownMenuItemVariants } from '@/components/molecules/DropdownMenuItem/DropdownMenuItem';
import FormInput from '@/components/molecules/FormInput';
import FormSelect from '@/components/molecules/FormSelect/FormSelect';
import clsx from 'clsx';
import { FC, useState } from 'react';
import { ChevronRight } from 'react-feather';

export interface InputFormData {
  vin: string;
  make: string;
  model: string;
  year: string;
}

interface VinInputProps {
  onSubmit: () => Promise<void>;
  toggleFormMode: () => void;
  formMode: 'vin' | 'makeAndModel';
  formData: InputFormData;
  updateFormData: (changes: Partial<InputFormData>) => void;
  allMakes: DropdownMenuOptionType[];
  allModels: Record<string, string[]>;
  invalidVinErrorMessage: string;
  submitted: boolean;
  /** Show a different style for this component if `true` by changing the bg color and text styles */
  alternate?: boolean;
}

export const VinInput: FC<VinInputProps> = ({
  alternate = false,
  onSubmit,
  formData,
  updateFormData,
  toggleFormMode,
  formMode,
  allMakes,
  allModels,
  invalidVinErrorMessage,
  submitted,
}) => {
  const [autoCompleteInput, setAutoCompleteInput] = useState(true);
  const [modelOptions, setModelOptions] = useState<DropdownMenuOptionType[]>(
    []
  );

  const handleVinChange = (newVin: string) => {
    if (newVin.length > 18) return;
    updateFormData({ vin: newVin.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() });
  };

  const defaultYearsOptions = [...Array(6)]
    .map((_, i) => ({
      text: (2020 + i).toString(),
      value: (2020 + i).toString(),
    }))
    .reverse();

  const handleUpdateMake = (newMake: string[]) => {
    updateFormData({ make: newMake[0] });
    const models = allModels[newMake[0]] || [];
    setModelOptions(models.map((model) => ({ text: model, value: model })));
  };

  const { year, make, model, vin } = formData;

  return (
    <div
      className={clsx(
        'flex flex-1 flex-col gap-s px-xl pt-xl ml:rounded-small',
        {
          'bg-brandElectric pb-[64px] ml:pb-xl': alternate,
          'bg-white/80 pb-xl': !alternate,
        }
      )}
    >
      <h3
        className={clsx('', {
          'hidden text-h2SemiBold ml:block': !alternate,
          'text-h3SemiBold': alternate,
        })}
      >
        {alternate ? 'Sell or trade your car' : 'Sell My Car'}
      </h3>
      <p className="mb-l text-h4Regular">
        {alternate
          ? 'Get offers from our network of verified and trusted dealerships today!'
          : 'Skip the hassle of a private sale. Get offers from dealers today!'}
      </p>
      <div
        className={clsx('flex flex-col gap-l ml:flex-row', {
          '!flex-col': formMode === 'makeAndModel',
        })}
      >
        {formMode === 'vin' ? (
          <div className="ml:basis-3/4">
            <FormInput
              onChange={handleVinChange}
              placeholder="Enter VIN Number"
              value={vin}
              invalid={invalidVinErrorMessage !== ''}
              errorMessage={invalidVinErrorMessage}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-l ml:basis-3/4">
            <FormSelect
              className="flex-1"
              dropDownMenuClassName="max-h-[240px]"
              placeholder="Select Year"
              variant={DropdownMenuItemVariants.SingleSelect}
              options={defaultYearsOptions}
              value={[year]}
              onChange={(e) => updateFormData({ year: e[0] })}
            />
            <FormSelect
              placeholder="Select Make(s)"
              disabled={allMakes.length === 0}
              variant={DropdownMenuItemVariants.SingleSelect}
              showCount={false}
              options={allMakes}
              value={[make]}
              autoCompleteInput={autoCompleteInput}
              setAutoCompleteInput={setAutoCompleteInput}
              onChange={(newMake) => handleUpdateMake(newMake)}
            />
            <FormSelect
              placeholder="Select Model(s)"
              options={modelOptions}
              disabled={modelOptions.length === 0 || !make}
              variant={DropdownMenuItemVariants.SingleSelect}
              value={[model]}
              autoCompleteInput={autoCompleteInput}
              setAutoCompleteInput={setAutoCompleteInput}
              showCount={false}
              onChange={(newModels) => updateFormData({ model: newModels[0] })}
            />
          </div>
        )}
        <div className="ml:mb-xl ml:basis-1/4">
          <Button
            aria-label="Get started"
            onClick={onSubmit}
            loading={submitted}
            variant={
              alternate ? ButtonVariants.Primary : ButtonVariants.Tertiary
            }
          >
            Get started
          </Button>
        </div>
      </div>
      <div
        role="button"
        className="flex flex-row items-center gap-xs self-center py-[13px] ml:self-start ml:py-0"
        onClick={toggleFormMode}
      >
        <p
          className={clsx('text-body2Medium', {
            'text-brandMidnight': alternate,
            'text-blue-medium': !alternate,
          })}
        >
          {formMode === 'vin' ? 'Or select make and model' : 'Or enter VIN'}
        </p>
        <ChevronRight
          size={16}
          className={alternate ? 'text-brandMidnight' : 'text-blue-medium'}
        />
      </div>
    </div>
  );
};
